import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';

const STATUS_LABELS = { pending:'Pending', in_progress:'In Progress', awaiting_info:'Awaiting Info', escalated:'Escalated', resolved:'Resolved', closed:'Closed' };
const PRIO_COLOR = { critical:'prio-critical', high:'prio-high', medium:'prio-medium', low:'prio-low' };
const ROLE_LABELS = { care_rep:'Care Rep', ict_staff:'ICT Staff', ict_manager:'ICT Manager', finance_officer:'Finance', super_admin:'Admin' };

function getFileUrl(storedName, apiUrl) {
  if (!storedName) return '#';
  if (storedName.startsWith('http://') || storedName.startsWith('https://')) return storedName;
  return `${apiUrl}/uploads/${storedName}`;
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('comments');
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [replyFiles, setReplyFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [updating, setUpdating] = useState(false);
  const [assignableStaff, setAssignableStaff] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL || '';

  const loadTicket = async () => {
    try {
      const res = await api.get(`/api/tickets/${id}`);
      setTicket(res.data);
      setStatusUpdate(res.data.status);
      setAssignTo(res.data.assigned_to || '');
    } catch { toast.error('Ticket not found'); navigate('/tickets'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    loadTicket();
    if (can('ict_staff','ict_manager','finance_officer','super_admin')) {
      api.get('/api/users/assignable').then(r => setAssignableStaff(r.data)).catch(() => {});
    }
  }, [id]);

  const postComment = async () => {
    if (!comment.trim() && replyFiles.length === 0) return toast.error('Please write a reply or attach a file');
    setPosting(true);
    try {
      const formData = new FormData();
      formData.append('content', comment.trim() || '(See attachment)');
      formData.append('is_internal', isInternal);
      replyFiles.forEach(f => formData.append('attachments', f));
      await api.post(`/api/tickets/${id}/comments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(isInternal ? '🔒 Internal note saved' : '📨 Reply sent');
      setComment(''); setReplyFiles([]);
      loadTicket();
    } catch { toast.error('Failed to post reply'); }
    finally { setPosting(false); }
  };

  const updateTicket = async () => {
    if (!comment.trim() && replyFiles.length === 0 && statusUpdate === ticket.status && assignTo === (ticket.assigned_to || '')) {
      return toast.error('No changes to save');
    }
    setUpdating(true);
    try {
      if (comment.trim() || replyFiles.length > 0) {
        const formData = new FormData();
        formData.append('content', comment.trim() || '(See attachment)');
        formData.append('is_internal', isInternal);
        replyFiles.forEach(f => formData.append('attachments', f));
        await api.post(`/api/tickets/${id}/comments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      if (statusUpdate !== ticket.status || assignTo !== (ticket.assigned_to || '')) {
        await api.put(`/api/tickets/${id}`, {
          status: statusUpdate !== ticket.status ? statusUpdate : undefined,
          assigned_to: assignTo !== (ticket.assigned_to || '') ? (assignTo || null) : undefined,
        });
      }
      toast.success('✅ All changes saved');
      setComment(''); setReplyFiles([]);
      loadTicket();
    } catch (err) { toast.error(err.response?.data?.message || 'Update failed'); }
    finally { setUpdating(false); }
  };

  const reopenTicket = async () => {
    try {
      await api.put(`/api/tickets/${id}`, { status: 'pending' });
      toast.success('Ticket reopened');
      loadTicket();
    } catch { toast.error('Failed to reopen'); }
  };

  if (loading) return <div className="loading">⏳ Loading ticket…</div>;
  if (!ticket) return null;

  const isStaff = can('ict_staff','ict_manager','finance_officer','super_admin','branch_manager');
  const age = Math.floor((new Date() - new Date(ticket.created_at)) / 86400000);

  return (
    <div>
      <button className="btn btn-ghost btn-sm" style={{ marginBottom:14 }} onClick={() => navigate('/tickets')}>← Back to Tickets</button>

      <div className="table-wrap">
        {/* Header */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', background:'var(--surface)', display:'flex', justifyContent:'space-between', alignItems:'flex-start', borderRadius:'14px 14px 0 0' }}>
          <div>
            <div style={{ fontFamily:'JetBrains Mono,monospace', fontSize:11, fontWeight:600, color:'var(--sky)', marginBottom:4 }}>{ticket.ticket_number}</div>
            <div style={{ fontFamily:'Sora,sans-serif', fontSize:16, fontWeight:700, color:'var(--text)', marginBottom:8 }}>{ticket.subject}</div>
            <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
              <span style={{ fontSize:11, color:'var(--text3)' }}>📅 {format(new Date(ticket.created_at),'dd MMM yyyy, HH:mm')}</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>🏢 {ticket.branch}</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>👤 {ticket.created_by_name}</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>📂 {ticket.category}</span>
              <span style={{ fontSize:11, color:'var(--text3)' }}>🏷️ {ticket.department === 'finance' ? 'Finance' : 'ICT'}</span>
              <span style={{ fontSize:11, color: age > 2 ? 'var(--red)' : 'var(--text3)' }}>⏱ {age === 0 ? 'Today' : `${age}d old`}</span>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
            <span className={`badge badge-${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span>
            <span style={{ fontSize:11 }}><span className={`prio-dot ${PRIO_COLOR[ticket.priority]}`} />{ticket.priority}</span>
            {ticket.sla_breached && <span style={{ fontSize:10, color:'var(--red)', fontWeight:700 }}>⚠️ SLA BREACHED</span>}
          </div>
        </div>

        {/* Body */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 280px' }}>
          <div style={{ padding:'18px 20px', borderRight:'1px solid var(--border)' }}>

            {/* Description */}
            <div style={{ background:'var(--sky-pale)', border:'1px solid var(--border)', borderLeft:'3px solid var(--sky)', borderRadius:'var(--radius)', padding:'12px 14px', marginBottom:16 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--sky2)', textTransform:'uppercase', letterSpacing:1, marginBottom:6, fontFamily:'JetBrains Mono,monospace' }}>Issue Description</div>
              <div style={{ fontSize:13, lineHeight:1.7, color:'var(--text)', whiteSpace:'pre-wrap' }}>{ticket.description}</div>
              {ticket.affected_staff && (
                <div style={{ marginTop:8, fontSize:11, color:'var(--text3)' }}><strong>Affected Staff/System:</strong> {ticket.affected_staff}</div>
              )}
            </div>

            {/* Tabs */}
            <div className="tabs">
              {[
                { key:'comments', label:`💬 Comments (${ticket.comments?.length || 0})` },
                { key:'attachments', label:`📎 Attachments (${ticket.attachments?.length || 0})` },
                { key:'audit', label:'📋 Audit Trail' },
              ].map(t => (
                <div key={t.key} className={`tab${tab===t.key?' active':''}`} onClick={() => setTab(t.key)}>{t.label}</div>
              ))}
            </div>

            {tab === 'comments' && (
              <div>
                {ticket.comments?.length === 0 && <div style={{ color:'var(--text3)', textAlign:'center', padding:'20px 0', fontSize:12 }}>No comments yet</div>}
                {ticket.comments?.map(c => (
                  <div key={c.id} className={`comment-box${c.is_internal?' internal':''}`}>
                    <div className="comment-head">
                      <span className="comment-author">
                        {c.author_name}
                        <span style={{ color:'var(--text3)', fontWeight:400 }}> ({ROLE_LABELS[c.author_role] || c.author_role})</span>
                        {c.is_internal && <span className="comment-tag">INTERNAL</span>}
                      </span>
                      <span className="comment-time">{format(new Date(c.created_at),'dd MMM, HH:mm')}</span>
                    </div>
                    <div className="comment-body">{c.content}</div>
                    {ticket.attachments?.filter(a => a.comment_id === c.id).map(a => (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, padding:'6px 10px', background:'var(--white)', borderRadius:6, border:'1px solid var(--border)' }}>
                        <span>📎</span>
                        <span style={{ fontSize:11, flex:1 }}>{a.original_name}</span>
                        <a href={getFileUrl(a.stored_name, API_URL)} target="_blank" rel="noreferrer" download={a.original_name}
                          className="btn btn-ghost btn-sm" style={{ fontSize:10 }}>⬇ Download</a>
                      </div>
                    ))}
                  </div>
                ))}

                {ticket.status === 'resolved' && can('care_rep') && (
                  <button className="btn btn-ghost btn-sm" style={{ color:'var(--warn)', borderColor:'var(--warn)', marginBottom:12 }} onClick={reopenTicket}>
                    🔄 Reopen — Issue Not Resolved
                  </button>
                )}

                <div style={{ marginTop:14, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:10 }}>
                    {isStaff ? 'Reply / Note' : 'Add a Reply'}
                  </div>
                  {isStaff && (
                    <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                      {[{key:false,label:'💬 Public Reply'},{key:true,label:'🔒 Internal Note'}].map(opt => (
                        <span key={String(opt.key)} className={`chip${isInternal===opt.key?' active':''}`}
                          style={{ fontSize:10, padding:'3px 10px' }}
                          onClick={() => setIsInternal(opt.key)}>{opt.label}</span>
                      ))}
                    </div>
                  )}
                  <textarea className="form-textarea" style={{ minHeight:80, fontSize:12, marginBottom:10 }}
                    placeholder={isInternal ? 'Internal note — team only…' : 'Type your reply here…'}
                    value={comment} onChange={e => setComment(e.target.value)} />
                  <div style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:'var(--text3)', marginBottom:5 }}>📎 Attach files to this reply (optional)</div>
                    <div className="upload-zone" style={{ padding:'10px 14px' }}
                      onClick={() => document.getElementById('reply-file-input').click()}>
                      <div style={{ fontSize:12, color:'var(--text3)' }}>Click to attach files</div>
                      <div style={{ fontSize:10, color:'var(--text4)', marginTop:2 }}>Images, PDFs, Word docs — max 10MB each</div>
                      {replyFiles.length > 0 && (
                        <div style={{ marginTop:6 }}>
                          {replyFiles.map((f,i) => (
                            <span key={i} style={{ fontSize:10, color:'var(--sky2)', background:'var(--sky-light)', display:'inline-block', padding:'2px 8px', borderRadius:4, margin:'2px' }}>
                              📄 {f.name}
                              <span style={{ cursor:'pointer', marginLeft:4, color:'var(--red)' }}
                                onClick={ev => { ev.stopPropagation(); setReplyFiles(replyFiles.filter((_,idx) => idx !== i)); }}>✕</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <input id="reply-file-input" type="file" multiple accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      style={{ display:'none' }} onChange={e => setReplyFiles([...e.target.files])} />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={isStaff ? updateTicket : postComment} disabled={posting || updating}>
                    {(posting || updating) ? 'Saving…' : isInternal ? '🔒 Save Note' : '📨 Send Reply'}
                  </button>
                  {!isStaff && <div className="info-box" style={{ marginTop:10 }}>ℹ️ You will be notified by email when the team responds.</div>}
                </div>
              </div>
            )}

            {tab === 'attachments' && (
              <div>
                {ticket.attachments?.length === 0 && <div style={{ color:'var(--text3)', textAlign:'center', padding:'20px 0', fontSize:12 }}>No attachments</div>}
                {ticket.attachments?.map(a => (
                  <div key={a.id} style={{ display:'flex', alignItems:'center', gap:10, padding:12, background:'var(--surface)', borderRadius:'var(--radius)', marginBottom:8, border:'1px solid var(--border)' }}>
                    <span style={{ fontSize:22 }}>📄</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{a.original_name}</div>
                      <div style={{ fontSize:11, color:'var(--text3)' }}>
                        {(a.file_size/1024).toFixed(1)} KB · {a.uploaded_by_name} · {format(new Date(a.created_at),'dd MMM, HH:mm')}
                        {a.comment_id && <span style={{ marginLeft:8, color:'var(--sky)', fontSize:10 }}>Attached in reply</span>}
                      </div>
                    </div>
                    <a href={getFileUrl(a.stored_name, API_URL)} target="_blank" rel="noreferrer" download={a.original_name}
                      className="btn btn-primary btn-sm">⬇ Download</a>
                  </div>
                ))}
              </div>
            )}

            {tab === 'audit' && (
              <div>
                {ticket.audit?.map(a => (
                  <div key={a.id} className="audit-item">
                    <span>•</span>
                    <div>
                      <strong style={{ color:'var(--text2)' }}>{a.action}</strong> — {a.user_name || 'System'}
                      <span style={{ marginLeft:8, fontFamily:'JetBrains Mono,monospace', fontSize:10, color:'var(--text3)' }}>
                        {format(new Date(a.created_at),'dd MMM yyyy, HH:mm')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info sidebar for all users */}
          <div style={{ padding:16 }}>
            {isStaff && (
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:14, marginBottom:14 }}>
                <div style={{ fontFamily:'Sora,sans-serif', fontSize:12, fontWeight:600, color:'var(--text)', marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ width:3, height:14, background:'linear-gradient(var(--sky),var(--orange))', borderRadius:2, display:'inline-block' }} />
                  Quick Update
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" style={{ fontSize:12 }} value={statusUpdate} onChange={e => setStatusUpdate(e.target.value)}>
                    {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-select" style={{ fontSize:12 }} value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                    <option value="">— Unassigned —</option>
                    {assignableStaff.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.full_name} ({s.role === 'finance_officer' ? '💰 Finance' : s.role === 'ict_manager' ? '💻 ICT Mgr' : '💻 ICT'})
                      </option>
                    ))}
                  </select>
                </div>
                <button className="btn btn-primary btn-sm" style={{ width:'100%' }} onClick={updateTicket} disabled={updating}>
                  {updating ? 'Saving…' : '💾 Save Changes'}
                </button>
              </div>
            )}

            {[
              { label:'Status', val: <span className={`badge badge-${ticket.status}`}>{STATUS_LABELS[ticket.status]}</span> },
              { label:'Assigned To', val: ticket.assigned_to_name || 'Unassigned' },
              { label:'Department', val: ticket.department === 'finance' ? '💰 Finance' : '💻 ICT' },
              { label:'Category', val: ticket.category },
              { label:'Branch', val: ticket.branch },
              ...(ticket.affected_staff ? [{ label:'Affected Staff', val: ticket.affected_staff }] : []),
              {
                label: 'Expected Resolution',
                val: ticket.category === 'ATM Card Request'
                  ? 'Production based'
                  : ticket.sla_deadline
                    ? format(new Date(ticket.sla_deadline), 'dd MMM yyyy, HH:mm')
                    : 'No SLA',
                color: ticket.sla_breached ? 'var(--red)' : ticket.status === 'resolved' || ticket.status === 'closed' ? 'var(--success)' : 'var(--text)',
                note: ticket.sla_breached ? '⚠️ Overdue' : ticket.status === 'resolved' || ticket.status === 'closed' ? '✅ Resolved' : null,
              },
              ...(ticket.resolved_at ? [{ label:'Resolved At', val: format(new Date(ticket.resolved_at), 'dd MMM yyyy, HH:mm') }] : []),
            ].map(f => (
              <div key={f.label} className="side-field">
                <div className="side-label">{f.label}</div>
                <div className="side-val" style={{ color: f.color || 'var(--text)' }}>
                  {f.val}
                  {f.note && <span style={{ fontSize:10, marginLeft:6 }}>{f.note}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
